import { useEffect, useState } from "react";
import AppModal from "../../components/AppModal";
import FilterBar, { FilterField } from "../../components/FilterBar";
import { formsService } from "../../services/apiService";
import { usePermission } from "../../hooks/usePermission";
import { showToast } from "../../utils/toast";
import { validateForm } from "../../utils/validate";
import { ACTIVE_STATUS_FILTERS, emptyMessageForStatus, statusFromFilter } from "../../utils/listFilters";

const STATUS_FILTERS = ACTIVE_STATUS_FILTERS("All forms");

const EMPTY = {
  id: null, name: "", description: "", est_time: "", sort_order: 0, is_active: 1,
  aside_title: "", aside_rules_text: "", aside_link: "", show_balance: 0,
};

const parseRules = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  }
  return [];
};

export default function Forms() {
  const { can } = usePermission();
  const canEdit = can("forms", "is_update");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = (params = {}) => {
    setLoading(true);
    formsService
      .getAll({
        search: params.search ?? search,
        status: statusFromFilter(params.filter ?? viewFilter, STATUS_FILTERS),
      })
      .then((res) => setRows(res?.data?.list || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load({ filter: viewFilter, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    load({ filter: viewFilter, search });
  };

  const onReset = (e) => {
    e.preventDefault();
    setViewFilter("all");
    setSearch("");
    load({ filter: "all", search: "" });
  };

  const handleStatusChange = (e) => {
    setViewFilter(e.target.value);
    setSearch("");
  };

  const emptyMessage = emptyMessageForStatus(viewFilter, {
    all: "No forms found",
    active: "No active forms",
    inactive: "No inactive forms",
  });

  const openEdit = (row) => {
    setForm({
      ...EMPTY,
      ...row,
      aside_title: row.aside_title || "",
      aside_link: row.aside_link || "",
      show_balance: row.show_balance ? 1 : 0,
      aside_rules_text: parseRules(row.aside_rules).join("\n"),
    });
    setModalOpen(true);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(e.currentTarget, [{ name: "name", value: form.name, label: "Name", required: true }]))
      return;
    setSubmitting(true);
    try {
      const aside_rules = form.aside_rules_text
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = { ...form, aside_rules };
      delete payload.aside_rules_text;
      const res = await formsService.update(payload);
      if (res?.success) {
        showToast("success", "Form updated");
        setModalOpen(false);
        load({ filter: viewFilter, search });
      } else showToast("error", res?.msg || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (row) => {
    const res = await formsService.updateStatus(row.id, row.is_active ? 0 : 1);
    if (res?.success) {
      showToast("success", "Status updated");
      load({ filter: viewFilter, search });
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <strong>Forms Catalog</strong>
      </div>
      <div className="card-body">
        <p className="text-muted small">
          The six interactive forms families can submit. Edit names, descriptions or
          turn a form on/off across the portal.
        </p>

        <FilterBar onSubmit={onSearch} onReset={onReset}>
          <FilterField id="form-status-filter" label="Show">
            <select
              id="form-status-filter"
              className="form-select"
              value={viewFilter}
              onChange={handleStatusChange}
            >
              {STATUS_FILTERS.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </FilterField>
          <FilterField id="form-search" label="Search">
            <input
              id="form-search"
              className="form-control"
              placeholder="Form name or description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterField>
        </FilterBar>

        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Form</th>
                <th>Description</th>
                <th>Est. time</th>
                <th>Status</th>
                <th style={{ width: 140 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} className="text-center py-4">Loading…</td></tr>)}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-muted">{emptyMessage}</td></tr>
              )}
              {!loading &&
                rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.sort_order}</td>
                    <td className="fw-semibold">{r.name}</td>
                    <td className="small text-muted">{r.description}</td>
                    <td>{r.est_time || "—"}</td>
                    <td>
                      {r.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      {canEdit && (
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>Edit</button>
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggle(r)}>
                            {r.is_active ? "Disable" : "Enable"}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <AppModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Edit Form"
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            <button type="submit" form="form-form" className="btn btn-primary" disabled={submitting}>
              {submitting ? "Saving…" : "Save Changes"}
            </button>
          </>
        }
      >
              <form id="form-form" onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input name="name" className="form-control" value={form.name} onChange={onChange} required />
                </div>
                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea name="description" className="form-control" rows={3} value={form.description || ""} onChange={onChange} />
                </div>
                <div className="row g-3">
                  <div className="col-6">
                    <label className="form-label">Est. time</label>
                    <input name="est_time" className="form-control" value={form.est_time || ""} onChange={onChange} />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Sort order</label>
                    <input name="sort_order" type="number" className="form-control" value={form.sort_order} onChange={onChange} />
                  </div>
                </div>
                <hr className="my-3" />
                <p className="text-muted small mb-2">
                  “Good to know” panel shown beside this form in the family portal.
                </p>
                <div className="mb-3">
                  <label className="form-label">Panel title</label>
                  <input name="aside_title" className="form-control" value={form.aside_title || ""} onChange={onChange} placeholder="e.g. How the discount works" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Panel bullet points <span className="text-muted">(one per line)</span></label>
                  <textarea name="aside_rules_text" className="form-control" rows={4} value={form.aside_rules_text || ""} onChange={onChange} placeholder={"Replacement days are within the same week.\nYour centre will confirm with you."} />
                </div>
                <div className="mb-3">
                  <label className="form-label">Policy link label <span className="text-muted">(optional)</span></label>
                  <input name="aside_link" className="form-control" value={form.aside_link || ""} onChange={onChange} placeholder="e.g. Read the Holiday Fees Policy" />
                </div>
                <div className="form-check">
                  <input type="checkbox" id="formShowBalance" className="form-check-input" name="show_balance" checked={!!form.show_balance} onChange={onChange} />
                  <label htmlFor="formShowBalance" className="form-check-label ms-1">Show holiday balance card</label>
                </div>
                <div className="form-check mt-3">
                  <input type="checkbox" id="formActive" className="form-check-input" name="is_active" checked={!!form.is_active} onChange={onChange} />
                  <label htmlFor="formActive" className="form-check-label ms-1">Active</label>
                </div>
              </form>
      </AppModal>
    </div>
  );
}
